// Detects whether we're on a StealthSeminar or ClickFunnels page, populates
// platform-specific custom fields from the store, and watches default form
// fields to capture user-typed PII into the store.
//
// CF runs garlic.js, which restores form values from localStorage on its own
// schedule. We re-write our custom fields multiple times after page load and
// listen for change events to overwrite anything garlic restores.

import type { UserStore } from './store';
import { getStore, updateStore, clearStoreKey } from './store';
import { sanitizePii } from './sanitize';

export type Platform = 'stealthseminar' | 'clickfunnels';

const SS_CUSTOM: Record<keyof Pick<UserStore, 'uuid'|'page_url'|'_fbp'|'_fbc'|'ip'|'user_agent'>, string> = {
  uuid:       'input[name="customFields.uuid"]',
  page_url:   'input[name="customFields.page_url"]',
  _fbp:       'input[name="customFields._fbp"]',
  _fbc:       'input[name="customFields._fbc"]',
  ip:         'input[name="customFields.ip"]',
  user_agent: 'input[name="customFields.user_agent"]',
};

const CF_CUSTOM: Record<keyof Pick<UserStore, 'uuid'|'page_url'|'_fbp'|'_fbc'|'ip'|'user_agent'>, string> = {
  uuid:       'input[data-custom-type="uuid"]',
  page_url:   'input[data-custom-type="page_url"]',
  _fbp:       'input[data-custom-type="_fbp"]',
  _fbc:       'input[data-custom-type="_fbc"]',
  ip:         'input[data-custom-type="ip"]',
  user_agent: 'input[data-custom-type="user_agent"]',
};

type CustomMap = typeof SS_CUSTOM;

export function detectPlatform(): Platform | null {
  if (document.querySelector('input[name^="customFields."]')) return 'stealthseminar';
  if (document.querySelector('input[data-custom-type]'))      return 'clickfunnels';
  return null;
}

export function customFieldMapFor(platform: Platform): CustomMap {
  return platform === 'stealthseminar' ? SS_CUSTOM : CF_CUSTOM;
}

// Write store values into the platform's custom fields. If a field exists on
// the page but the store has no value for it, we *blank* the field — that's
// the "remove the value … ones that had been populated by a previous session
// via garlic.js" behavior.
export function fillCustomFields(map: CustomMap, store: UserStore): void {
  for (const [key, sel] of Object.entries(map) as Array<[keyof CustomMap, string]>) {
    const inputs = document.querySelectorAll<HTMLInputElement>(sel);
    if (!inputs.length) continue;
    const value = store[key];
    inputs.forEach(el => {
      if (typeof value === 'string' && value) {
        if (el.value !== value) el.value = value;
      } else if (el.value) {
        el.value = '';
      }
    });
  }
}

// Schedule multiple fill passes to win the race against garlic.js (CF) or
// the SS SPA re-rendering forms. Also installs a delegated listener that
// re-writes our value whenever an input fires change/input.
//
// Optional `beforeRefill` hook runs on each pass so callers can update the
// store right before the refill writes values to fields — used to pull
// freshly-set _fbp/_fbc cookies into the store as Meta's pixel sets them
// asynchronously after our initial boot.
export function scheduleAntiGarlicRefills(map: CustomMap, beforeRefill?: () => void): void {
  const refill = () => {
    try { beforeRefill?.(); } catch (e) { console.warn('[capi-beacon] beforeRefill error', e); }
    fillCustomFields(map, getStore());
  };

  refill();
  for (const ms of [50, 200, 500, 1500, 3500, 7500]) {
    setTimeout(refill, ms);
  }

  const reapplyHandler = (ev: Event) => {
    const target = ev.target as HTMLInputElement | null;
    if (!target || target.tagName !== 'INPUT') return;
    for (const [key, sel] of Object.entries(map) as Array<[keyof CustomMap, string]>) {
      if (!target.matches(sel)) continue;
      const want = getStore()[key];
      if (typeof want === 'string' && want) {
        if (target.value !== want) target.value = want;
      } else if (target.value) {
        target.value = '';
      }
      break;
    }
  };
  document.addEventListener('change', reapplyHandler, true);
  document.addEventListener('input', reapplyHandler, true);
}

// Default-field watcher: listens for input/change/blur on the given fields,
// sanitizes user-typed values, and merges them into the store.
type DefaultFieldSpec = {
  selector: string;
  // What field of the raw PII bag this maps to before sanitization.
  // 'name' is special — sanitizePii will split it into first_name/last_name.
  key: 'name' | 'email' | 'phone' | 'city' | 'state' | 'zip' | 'country';
};

const SS_DEFAULTS: DefaultFieldSpec[] = [
  { selector: 'input[name="name"]',        key: 'name'  },
  { selector: 'input[name="email"]',       key: 'email' },
  { selector: 'input[type="tel"].b_phone', key: 'phone' },
];

const CF_DEFAULTS: DefaultFieldSpec[] = [
  { selector: 'input[name="name"]',              key: 'name'    },
  { selector: 'input[name="email"]',             key: 'email'   },
  { selector: 'input[name="phone"]',             key: 'phone'   },
  { selector: 'input[name="shipping_city"]',     key: 'city'    },
  { selector: 'input[name="shipping_state"]',    key: 'state'   },
  { selector: 'input[name="shipping_zip"]',      key: 'zip'     },
  { selector: 'input[name="shipping_country"]',  key: 'country' },
];

function defaultsFor(platform: Platform): DefaultFieldSpec[] {
  return platform === 'stealthseminar' ? SS_DEFAULTS : CF_DEFAULTS;
}

// Capture user-typed values via event delegation so dynamically inserted
// inputs (e.g. CF multi-step forms) are covered.
export function watchDefaults(platform: Platform, onUpdate?: () => void): void {
  const specs = defaultsFor(platform);

  const handler = (ev: Event) => {
    const target = ev.target as HTMLInputElement | null;
    if (!target || target.tagName !== 'INPUT') return;

    for (const spec of specs) {
      if (!target.matches(spec.selector)) continue;
      const raw = target.value?.trim();
      if (!raw) return;
      const sanitized = sanitizePii({ [spec.key]: raw });
      if (Object.keys(sanitized).length) {
        updateStore(sanitized);
        onUpdate?.();
      }
      return;
    }
  };

  document.addEventListener('input', handler, true);
  document.addEventListener('change', handler, true);
  document.addEventListener('blur', handler, true);
}

// CF: the user advances by clicking <a href="#submit-form-2step">. Treat
// that as a "flush" event — re-scan all current default-field values and
// re-fill the custom fields with whatever's freshly captured.
export function watchCFSubmitTrigger(map: CustomMap, onSubmit?: () => void): void {
  document.addEventListener('click', (ev: Event) => {
    const target = ev.target as Element | null;
    if (!target) return;
    const link = target.closest('a[href="#submit-form-2step"]');
    if (!link) return;

    flushDefaults('clickfunnels');
    fillCustomFields(map, getStore());
    onSubmit?.();
  }, true);
}

// Best-effort: scan all currently-rendered default fields once and merge.
export function flushDefaults(platform: Platform): void {
  const specs = defaultsFor(platform);
  const raw: Record<string, string> = {};
  for (const spec of specs) {
    const el = document.querySelector<HTMLInputElement>(spec.selector);
    const v = el?.value?.trim();
    if (v) raw[spec.key] = v;
  }
  if (Object.keys(raw).length) {
    const sanitized = sanitizePii(raw as Parameters<typeof sanitizePii>[0]);
    if (Object.keys(sanitized).length) updateStore(sanitized);
  }
}

// Re-export so callers can drop a stale key when garlic restores something
// we have no current value for.
export { clearStoreKey };
